import React, { useState } from 'react';
import ScopeField from './ScopeField';
import { fetchReport, downloadReport } from '../api/reports';
import DateField from './DateField';
import './ReportRunner.css';

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const METRICS = [
  { key: 'sales', label: 'Ventes' },
  { key: 'payout', label: 'Payout (gains)' },
  { key: 'profit', label: 'Profit / Marge' },
  { key: 'offline', label: 'Transactions offline' },
  { key: 'reconciliation', label: 'Réconciliation financière' },
];
const GROUP_BY = [
  { key: 'bank', label: 'Agent / POS' },
  { key: 'group', label: 'Group' },
  { key: 'supervisor', label: 'Superviseur' },
  { key: 'tirage', label: 'Loterie' },
  { key: 'day', label: 'Jour' },
  { key: 'company', label: 'Company' },
];
const SCOPES = [
  { key: 'All', label: 'Tout' },
  { key: 'Group', label: 'Groupe' },
  { key: 'Supervisor', label: 'Superviseur' },
  { key: 'Bank', label: 'Banque' },
];

const MONEY = ['vendu', 'payout', 'commission', 'profit'];
const fmtCell = (k, v) =>
  MONEY.includes(k)
    ? (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(v ?? '');

// showCompanyGroupBy : le superadmin peut regrouper par company ; une company non.
// showSupervisorGroupBy : idem pour "Superviseur" — un superviseur connecte au
// portail superviseur (endpoint="/reports/supervisor/") n'a que lui-meme comme
// superviseur, regrouper par ca est toujours 1 seule ligne, retire de son UI.
// showScopeFilter (demande explicite 2026-09-19, "fait pareil pour rapport
// general") : filtre Tout/Groupe/Superviseur/Banque, meme comportement que
// TerminalReportPage/BankReportPage/ListeVentesPage (auto-resolution au
// blur). Desactive par defaut pour le portail superviseur (ses banks/groups
// ne sont pas cherchables via /bank-groups/, /supervisors/, /banks/ — ces
// endpoints n'acceptent que l'auth company/staff, pas SupervisorSession) et
// pour le superadmin (recherche non scopee par company, pas encore geree
// par ces memes endpoints) — reste actif uniquement cote company (defaut).
const ReportRunner = ({
  showCompanyGroupBy = false, showSupervisorGroupBy = true, showScopeFilter = true, endpoint = '/reports/',
}) => {
  const [metric, setMetric] = useState('sales');
  const [groupBy, setGroupBy] = useState('bank');
  const [dateFrom, setDateFrom] = useState(daysAgoISO(6));
  const [dateTo, setDateTo] = useState(todayISO());
  const [filter, setFilter] = useState('All');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const groupOptions = GROUP_BY
    .filter((g) => showCompanyGroupBy || g.key !== 'company')
    .filter((g) => showSupervisorGroupBy || g.key !== 'supervisor');
  const params = () => ({
    metric,
    group_by: metric === 'reconciliation' ? undefined : groupBy,
    date_from: dateFrom,
    date_to: dateTo,
    filter: showScopeFilter && filter !== 'All' ? filter : undefined,
    scope: showScopeFilter && filter !== 'All' ? scopeId : undefined,
  });

  const run = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    try {
      setData(await fetchReport(params(), endpoint));
    } catch (err) {
      setError('Chargement impossible.');
    } finally {
      setLoading(false);
    }
  };

  const dl = async (fmt) => {
    setError('');
    try {
      await downloadReport(params(), fmt, endpoint);
    } catch (err) {
      setError('Export impossible.');
    }
  };

  return (
    <div className="rr">
      {showScopeFilter && (
        <div className="rr-scope-row">
          {SCOPES.map((s) => (
            <label key={s.key} className="rr-scope-label">
              <input
                type="radio"
                name="rr-scope"
                checked={filter === s.key}
                onChange={() => { setFilter(s.key); setScopeId(''); setScopeName(''); }}
              />
              {s.label}
            </label>
          ))}
          {filter !== 'All' && (
            <>
              <ScopeField
                filter={filter}
                id={scopeId}
                name={scopeName}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                inputClass="rr-scope-input"
                iconClass="rr-scope-icon"
                nameClass="rr-scope-name"
              />
            </>
          )}
        </div>
      )}

      <form className="rr-filters" onSubmit={run}>
        <label>
          Type
          <select value={metric} onChange={(e) => setMetric(e.target.value)}>
            {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </label>
        <label>
          Regrouper par
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            disabled={metric === 'reconciliation'}
          >
            {groupOptions.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </label>
        <label>Du <DateField value={dateFrom} onChange={setDateFrom} /></label>
        <label>Au <DateField value={dateTo} onChange={setDateTo} /></label>
        <button type="submit" disabled={loading}>{loading ? '…' : 'Chercher'}</button>
      </form>

      {error && <p className="rr-error">{error}</p>}

      {data && (
        <>
          <div className="rr-exports">
            <span>Exporter :</span>
            <button type="button" onClick={() => dl('csv')}>CSV</button>
            <button type="button" onClick={() => dl('xlsx')}>Excel</button>
            <button type="button" onClick={() => dl('pdf')}>PDF</button>
          </div>

          <div className="rr-table-wrap">
            <table className="rr-table">
              <thead>
                <tr>{data.columns.map((c) => <th key={c}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={data.columns.length} className="rr-empty">Aucune donnée.</td></tr>
                )}
                {data.rows.map((row, i) => (
                  <tr key={i} className={row._subtotal ? 'rr-subtotal' : ''}>
                    {data.keys.map((k) => (
                      <td key={k} className={MONEY.includes(k) ? 'rr-num' : ''}>{fmtCell(k, row[k])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {data.totals && (
                <tfoot>
                  <tr className="rr-total">
                    {data.keys.map((k) => (
                      <td key={k} className={MONEY.includes(k) ? 'rr-num' : ''}>{fmtCell(k, data.totals[k])}</td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <p className="rr-meta">
            {data.meta.company} · du {data.meta.date_from} au {data.meta.date_to} · généré {data.meta.generated_at}
          </p>
        </>
      )}
    </div>
  );
};

export default ReportRunner;
